import { Textarea } from '@/components/ui/Textarea';
import type { MessageChannel } from '@/types/messaging';
import {
  REMINDER_MAX_LENGTH,
  REMINDER_PLACEHOLDERS,
  REMINDER_PLACEHOLDER_HINT,
  SMS_PART_LENGTH,
} from '../constants';

interface ReminderMessageFieldProps {
  message: string;
  onChange: (message: string) => void;
  channel: MessageChannel;
}

/** How many SMS parts a message is billed as. */
export function smsParts(message: string): number {
  return Math.max(1, Math.ceil(message.length / SMS_PART_LENGTH));
}

/**
 * The reminder's text, the placeholders the server fills in per family, and
 * who it reaches — the office's reminder dialog and the class teacher's alike,
 * so both write to families under the same rules.
 */
export function ReminderMessageField({ message, onChange, channel }: ReminderMessageFieldProps) {
  const parts = smsParts(message);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Textarea
        label="Reminder"
        rows={4}
        maxLength={REMINDER_MAX_LENGTH}
        value={message}
        onChange={(event) => onChange(event.target.value)}
        hint={
          channel === 'SMS'
            ? `${message.length}/${REMINDER_MAX_LENGTH} characters · billed as ${parts} ${parts === 1 ? 'message' : 'messages'} each`
            : `${message.length}/${REMINDER_MAX_LENGTH} characters`
        }
      />

      {/* The placeholders, offered rather than documented elsewhere: one
          nobody knows about is one nobody uses. Clicking appends it. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="lbl">Fill in per family</span>
        {REMINDER_PLACEHOLDERS.map((placeholder) => (
          <button
            key={placeholder}
            type="button"
            title={`Insert ${REMINDER_PLACEHOLDER_HINT[placeholder]}`}
            onClick={() => onChange(`${message}{${placeholder}}`)}
            className="pill bg-surface-2 font-mono text-[12px] text-ink-2 transition-colors hover:bg-accent-soft hover:text-accent"
          >
            {`{${placeholder}}`}
          </button>
        ))}
      </div>

      <p className="text-xs text-ink-3">
        Sent to the parent named on the student's record — the primary contact, or the father or
        mother if there is no number against that one. Families cannot reply, so put the office
        number in the text if you want an answer.
      </p>
    </div>
  );
}
